import { useUpdateLabel } from "@/hooks/use-labels";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type LabelEditDialogProps = {
  open: boolean;
  onClose: () => void;
  labelId: string;
  currentName: string;
};

/** ラベル編集モーダル */
export const LabelEditDialog = ({
  open,
  onClose,
  labelId,
  currentName,
}: LabelEditDialogProps) => {
  const [name, setName] = useState(currentName);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateLabel = useUpdateLabel();

  useEffect(() => {
    if (open) {
      setName(currentName);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open, currentName]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;

    updateLabel.mutate(
      { id: labelId, data: { name: trimmed } },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* オーバーレイ */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />

      {/* ダイアログ */}
      <div className="relative mx-4 w-full max-w-sm rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">ラベルを編集</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ラベル名"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={!name.trim() || updateLabel.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {updateLabel.isPending ? "更新中..." : "更新"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
